/* eslint-disable jira/filename-case */
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/index.tsx';
import { isConflictError } from '@jira/polaris__lib-errors/src/isPermissionError.tsx';
import { type IssueTypeCreateResponse, performCreateIssueType } from './performCreateIssueType.tsx';
import type {
	IssueTypeModifiableProps,
	IssueTypeWithPinnedFields,
	State,
} from './IssueType.types.tsx';
import { fetchIssueTypesForProjects } from './fetchIssueTypesForProjects.tsx';

type ProjectId = string;

const transformIssueType = (rest: IssueTypeCreateResponse): IssueTypeWithPinnedFields => ({
	id: String(rest.id),
	name: rest.name,
	entityId: rest.entityId,
	description: rest.description,
	avatarId: String(rest.avatarId),
	avatarUrl: rest.avatarUrl,
	hierarchyLevel: rest.hierarchyLevel,
	operation: {
		deletable: true,
		editable: true,
	},
	pinnedFields: [],
});

export const createIssueType =
	(projectId: ProjectId, data: IssueTypeModifiableProps) =>
	async ({
		setState,
		getState,
		dispatch,
	}: StoreActionApi<State>): Promise<IssueTypeWithPinnedFields> => {
		experience.issueTypes.issueTypeCreate.start();

		try {
			const result = await performCreateIssueType(projectId, {
				name: data.name,
				description: data.description,
				avatarId: Number(data.avatarId),
			});

			const newIssueType = transformIssueType(result);

			const state = getState();

			setState({
				issueTypesById: {
					...state.issueTypesById,
					[newIssueType.id]: newIssueType,
				},
				issueTypesByProjectId: {
					...state.issueTypesByProjectId,
					[projectId]: [...(state.issueTypesByProjectId[projectId] ?? []), newIssueType.id],
				},
			});

			experience.issueTypes.issueTypeCreate.success();

			dispatch(fetchIssueTypesForProjects([projectId]));

			return newIssueType;
		} catch (maybeError) {
			const error =
				maybeError instanceof Error ? maybeError : new Error('Error while creating issue type');

			if (isClientFetchError(error) || isConflictError(error)) {
				experience.issueTypes.issueTypeCreate.abort();
			} else {
				experience.issueTypes.issueTypeCreate.failure(error);
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.controllers.issue-types.actions.create-issue-type',
				'Failed to create issue type',
				error,
			);

			throw error;
		}
	};
